import React from 'react';

import DynamicColumnsTableColumnHeader from 'components/DynamicColumnsTable/DynamicColumnsTableColumnHeader';
import { CommunityContractBenefitGrouped, ContractType } from 'types/contracts';

import ContractBenefitsTableItem from './ContractBenefitsTableItem';

const COMMUNITY_COLUMN_OFFSET = 1;

export const getContractBenefitsTableColumns = ({
  community,
  competitors,
  filteredCompetitors,
  displayedNumberOfCompetitors,
  competitorsMinIndex,
  competitorsMaxIndex,
  scrollCompetitorsLeft,
  scrollCompetitorsRight,
}: {
  community: any;
  competitors: any[];
  filteredCompetitors: any[];
  competitorsMinIndex: number;
  competitorsMaxIndex: number;
  displayedNumberOfCompetitors: number;
  scrollCompetitorsLeft: () => void;
  scrollCompetitorsRight: () => void;
}) => {
  const communitiesList = [community, ...filteredCompetitors].filter((community) => Boolean(community));

  const disableLeftScroll = competitorsMinIndex === 0;
  const disableRightScroll =
    competitorsMaxIndex === (competitors?.length || 0) - 1 || competitors?.length + 1 <= competitorsMaxIndex;

  const columns: any[] = [
    {
      title: '',
      render: (value: any, record: any) => <div>{record?.rowName}</div>,
    },
    {
      title: communitiesList?.length ? communitiesList[0].comunity_name : '',
      render: (value: any, record: any) => <ContractBenefitsTableItem record={record} index={0} />,
    },
  ];

  if (communitiesList.length) {
    columns.push(
      ...communitiesList.slice(1).map((c, index: number) => ({
        title: () => (
          <DynamicColumnsTableColumnHeader
            index={index}
            community={c}
            filteredCompetitorsCount={filteredCompetitors.length}
            totalCompetitorsCount={competitors?.length ?? 0}
            scrollLeft={scrollCompetitorsLeft}
            scrollRight={scrollCompetitorsRight}
            disableLeft={disableLeftScroll}
            disableRight={disableRightScroll}
            displayedNumberOfCompetitors={displayedNumberOfCompetitors}
          />
        ),
        render: (value: any, record: any) => {
          const columnIndex = COMMUNITY_COLUMN_OFFSET + competitorsMinIndex + index;
          return <ContractBenefitsTableItem record={record} index={columnIndex} />;
        },
      }))
    );
  }

  return columns;
};

export const getContractBenefitsTableData = (
  contracts: ContractType[],
  benefits: any,
  communities: any[],
  contractTypeFilter?: number | number[]
) => {
  const groupedData: any = {};

  if (!benefits) {
    return [];
  }

  benefits
    .sort((a: any, b: any) => {
      const aId = a?.category_id ?? Number.MAX_SAFE_INTEGER;
      const bId = b?.category_id ?? Number.MAX_SAFE_INTEGER;
      return aId - bId;
    })
    .forEach((benefit: any) => {
      const key = `${benefit.community_id}-${benefit.contract_id}`;

      if (!groupedData[key]) {
        groupedData[key] = {
          community_id: benefit.community_id,
          contract_id: benefit.contract_id,
          groupedBenefits: {},
        };
      }

      const category = benefit.category_name;

      if (!groupedData[key].groupedBenefits[category]) {
        groupedData[key].groupedBenefits[category] = [];
      }

      groupedData[key].groupedBenefits[category].push(benefit.benefit);
    });

  const groupedBenefits: CommunityContractBenefitGrouped[] = Object.values(groupedData);

  const contractTypesFilterSet = (() => {
    if (contractTypeFilter === undefined) {
      return undefined;
    }

    const rawFilterValues = Array.isArray(contractTypeFilter) ? contractTypeFilter : [contractTypeFilter];
    if (!rawFilterValues.length) {
      return undefined;
    }

    const resolvedTypes = rawFilterValues
      .map((filterValue) => {
        const contractById = contracts?.find((contract: ContractType) => contract.id === filterValue);

        if (contractById) {
          return contractById.type;
        }

        return filterValue;
      })
      .filter((value): value is number => typeof value === 'number');

    return resolvedTypes.length ? new Set(resolvedTypes) : undefined;
  })();

  const filteredContracts = contractTypesFilterSet
    ? contracts?.filter((contract: ContractType) => contractTypesFilterSet.has(contract.type))
    : contracts;

  const rows: any = (filteredContracts ?? []).map((contract) => ({
    id: contract.id,
    rowName: contract.name,
    rowItems: communities?.map((community: any) =>
      groupedBenefits?.find((benefit) => {
        return benefit.community_id === community.id && benefit.contract_id === contract.id;
      })
    ),
  }));

  const filteredRows = rows.filter((row: any) => row.rowItems?.some((rowItem: any) => Boolean(rowItem))); // remove empty rows

  const titleRow = {
    id: 0,
    rowName: 'Contract Types',
  };

  return [titleRow, ...filteredRows];
};
